import { z } from 'zod';

/**
 * Clés de l'installation saisies depuis la console.
 *
 * **La valeur n'est dans aucun de ces contrats.** L'écriture l'accepte, la lecture ne rend que
 * « posée » et, au plus, les quatre derniers caractères. La ligne en base est la clé effective :
 * `source` vaut `database` dès qu'elle existe. `environment` ne décrit que l'instant d'avant la
 * copie d'amorce ; après, ce n'est plus la source.
 */

export const installationSecretNames = [
  'stripe_secret_key',
  'stripe_webhook_secret',
  'openai_api_key',
  'geo_gateway_api_key',
] as const;
export type InstallationSecretName = (typeof installationSecretNames)[number];

export const installationSecretSources = ['environment', 'database'] as const;
export type InstallationSecretSource = (typeof installationSecretSources)[number];

/** Une clé, telle que la console peut la montrer. Jamais la valeur. */
export interface InstallationSecretSlotDto {
  set: boolean;
  /** Quatre derniers caractères de la clé **effective**, ou nul. Le support ne les reçoit pas. */
  last4: string | null;
  /** D'où vient la clé effective. Nul quand aucune n'est posée. */
  source: InstallationSecretSource | null;
}

export interface InstallationSecretsDto {
  stripeSecretKey: InstallationSecretSlotDto;
  stripeWebhookSecret: InstallationSecretSlotDto;
  openaiApiKey: InstallationSecretSlotDto;
  geoGatewayApiKey: InstallationSecretSlotDto;
}

/**
 * Une valeur non vide, bornée. Le vide n'est pas une écriture : omettre le champ laisse la clé
 * en place. L'effacement est l'autre route.
 */
const secretValueSchema = z.string().trim().min(8).max(512);

export const installationSecretsUpdateSchema = z
  .object({
    stripeSecretKey: secretValueSchema.optional(),
    stripeWebhookSecret: secretValueSchema.optional(),
    openaiApiKey: secretValueSchema.optional(),
    geoGatewayApiKey: secretValueSchema.optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.stripeSecretKey !== undefined ||
      value.stripeWebhookSecret !== undefined ||
      value.openaiApiKey !== undefined ||
      value.geoGatewayApiKey !== undefined,
    { message: 'aucune clé à enregistrer' },
  );
export type InstallationSecretsUpdate = z.infer<typeof installationSecretsUpdateSchema>;

export const installationSecretParamSchema = z
  .object({ name: z.enum(installationSecretNames) })
  .strict();
